  import React, { useEffect, useState } from 'react';
  import './email-modal.css';
  import NavigationButton from './navigation-button';
  import axios from 'axios';
  import { renderEmailHtml } from '../../utils/emailTemplate';

  const SENDMAIL_URL =
    import.meta.env.VITE_SENDMAIL_URL ||
    (import.meta.env.DEV
      ? "/api/sendmail"
      : "https://www.addict-paris.fr/simulateur/email/addictSendmail/sendmail.php");

  const DEBUG_FORM_DATA = import.meta.env.DEV
    ? { nom: 'FTZ', prenom: 'Seb', email: 'sebastien@digeek.fr', telephone: '0687370727', rgpd: true }
    : { nom: '', prenom: '', email: '', telephone: '', rgpd: false };

  const EmailModal = ({ isOpen, onClose, onSendEmail }) => {
    const [formData, setFormData] = useState(DEBUG_FORM_DATA);

    const [errors, setErrors] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    useEffect(() => {
      const { nom, email, rgpd } = formData;
      setIsFormValid(nom.trim() !== '' && email.trim() !== '' && rgpd);
    }, [formData]);

    useEffect(() => {
      const handleEscKey = (event) => {
        if (isOpen && event.key === 'Escape') {
          onClose();
        }
      };

      window.addEventListener('keydown', handleEscKey);
      return () => {
        window.removeEventListener('keydown', handleEscKey);
      };
    }, [onClose, isOpen]);

    const handleContentClick = (e) => {
      e.stopPropagation();
    };

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });

      if (errors[name]) {
        setErrors({
          ...errors,
          [name]: ''
        });
      }
    };

    const validateForm = () => {
      const newErrors = {};
      
      if (!formData.nom.trim()) {
        newErrors.nom = 'Le nom est requis';
      }
      
      if (!formData.email.trim()) {
        newErrors.email = 'L\'email est requis';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'L\'email n\'est pas valide';
      }
      
      if (!formData.rgpd) {
        newErrors.rgpd = 'Vous devez accepter les conditions';
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const sendEmail = async () => {
      try {
        setIsSubmitting(true);
        
        const summaryData = JSON.parse(localStorage.getItem('addict_summary_data') || '{}');
        
        let template = '';
        
        try {
          let templateUrl = `${import.meta.env.BASE_URL || '/'}assets/html/email-html-addict.html`;
          
          const templateResponse = await fetch(templateUrl);

          if (!templateResponse.ok) {
            templateUrl = '/assets/html/email-html-addict.html';
            
            const fallbackResponse = await fetch(templateUrl);
          
            if (!fallbackResponse.ok) {
              throw new Error(`Erreur lors de la récupération du template: ${fallbackResponse.status} ${fallbackResponse.statusText}`);
            }
            template = await fallbackResponse.text();
          } else {
            template = await templateResponse.text();
          }
          
        
          
        } catch (error) {
          console.error('Erreur lors de la récupération du template:', error);
          template = '<html><body><h1>Votre simulation d\'abonnement Addict Paris</h1><p>Merci pour votre simulation.</p></body></html>';
        }
        
        const fullName = formData.prenom ? `${formData.prenom} ${formData.nom}` : formData.nom;
        
        const personalizedTemplate = personalizeTemplate(template, summaryData, {
          prenom: formData.prenom || '',
          nom: formData.nom || '',
          fullName,
        });
        
        const formDataObj = new FormData();
        formDataObj.append('to', formData.email);
        formDataObj.append('subject', `Votre simulation d'abonnement ${summaryData.serviceType || 'personnalisé'} - Addict Paris`);
        formDataObj.append('html', personalizedTemplate);
        
        formDataObj.append('userName', fullName);
        formDataObj.append('userPhone', formData.telephone);

        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: SENDMAIL_URL,
          data: formDataObj
        };
        
        const timeoutId = setTimeout(() => {
          console.error('Timeout: La requête a pris trop de temps');
          setSubmitStatus('error');
          if (onSendEmail) {
            onSendEmail(false, 'Le serveur met trop de temps à répondre. Veuillez réessayer.');
          }
          setIsSubmitting(false);
        }, 30000);
        
        const response = await axios.request(config);
        
        clearTimeout(timeoutId);

        
        const isSuccess = response.status === 200 && (
          (response.data && response.data.status === 'success') ||
          (response.data && response.data.success === true) ||
          (response.data && response.data.message && (
            response.data.message.includes('Queued') || 
            response.data.message.includes('Thank you') ||
            response.data.message.includes('success')
          )) ||
          (typeof response.data === 'string' && (
            response.data.includes('Queued') || 
            response.data.includes('Thank you') ||
            response.data.includes('success')
          ))
        );
        
        if (isSuccess) {
        
          setSubmitStatus('success');
          
          if (onSendEmail) {
            onSendEmail(true);
          }
        } else {
          throw new Error(response.data?.message || 'Réponse inattendue du serveur');
        }
      } catch (error) {
        console.error('Erreur lors de l\'envoi de l\'email:', error);
        setSubmitStatus('error');
        
        const errorMessage = error.response?.data?.message || 
                            error.message || 
                            'Une erreur s\'est produite lors de l\'envoi de l\'email';
        
        if (onSendEmail) {
          onSendEmail(false, errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    const personalizeTemplate = (template, summaryData, contact) =>
      renderEmailHtml({ template, summaryData, contact });

    const handleSubmit = () => {
      if (validateForm()) {
        sendEmail();
      }
    };

    if (!isOpen) return null;

    return (
      <div 
        className="email-modal-overlay" 
        onClick={onClose} 
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        <div className="email-modal-content" onClick={handleContentClick}>
          <button className="close-welcome-button" onClick={onClose}>
            X
          </button>
          
          <div className="email-modal-header">
            <h2 className="email-modal-title">
              VOS COORDONNÉES
            </h2>
          </div>
          
          {submitStatus === 'success' ? (
            <div className="email-modal-success">
              <div className="success-icon">✓</div>
              <h3>Email envoyé avec succès!</h3>
              <p>Vous allez recevoir votre simulation d'abonnement.</p>
            </div>
          ) : submitStatus === 'error' ? (
            <div className="email-modal-error">
              <div className="error-icon">⚠</div>
              <h3>Erreur lors de l'envoi</h3>
              <p>Veuillez réessayer.<br />Si le problême persiste vous pouvez nous contacter : contact@addict-paris.fr.</p>
              <div className="error-buttons">
                <button 
                  className="retry-button"
                  onClick={() => {
                    setSubmitStatus(null);
                    handleSubmit();
                  }}
                >
                  Réessayer
                </button>
                <button 
                  className="modify-button"
                  onClick={() => {
                    setSubmitStatus(null);
                  }}
                >
                  Modifier les informations
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="email-modal-body">
                <div className="form-group">
                  <label htmlFor="nom">Nom<span className="required">*</span></label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={errors.nom ? 'input-error' : ''}
                  />
                  {errors.nom && <div className="error-message">{errors.nom}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="prenom">Prénom</label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email<span className="required">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'input-error' : ''}
                  />
                  {errors.email && <div className="error-message">{errors.email}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="telephone">Téléphone</label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      name="rgpd"
                      checked={formData.rgpd}
                      onChange={handleChange}
                      className={errors.rgpd ? 'checkbox-error' : ''}
                    />
                    <span className="checkmark"></span>
                    <span className="checkbox-label">
                      J'accepte que mes données soient transmises et utilisées dans le cadre de ma demande de tarif d'abonnement.
                    </span>
                  </label>
                  {errors.rgpd && <div className="error-message">{errors.rgpd}</div>}
                </div>
              </div>
              
              <div className="email-modal-footer">
                <div className="email-modal-navigation-button-container">
                  <NavigationButton
                    isEnabled={isFormValid && !isSubmitting}
                    onClick={handleSubmit}
                    label={isSubmitting ? "Envoi en cours..." : "Envoyer l'email"}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  export default EmailModal;
